import {
  prepareChildrensBookManuscriptFlowApplication,
} from "./childrensBookManuscriptFlowProjection";

import {
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";


function clean(
  value
) {
  return String(
    value ?? ""
  ).trim();
}


function safeArray(
  value
) {
  return Array.isArray(
    value
  )
    ? value
    : [];
}


function safeObject(
  value
) {
  return (
    value &&
    typeof value ===
      "object" &&
    !Array.isArray(
      value
    )
  )
    ? value
    : {};
}


function entriesWithActiveEdit({
  entries,
  activeUnitId,
  changedFields,
}) {
  const manuscriptEntries =
    safeArray(
      entries
    );

  const unitId =
    clean(
      activeUnitId
    );

  const fields =
    safeObject(
      changedFields
    );

  const exists =
    manuscriptEntries.some(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        unitId
    );

  if (exists) {
    return manuscriptEntries.map(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        unitId
          ? {
              ...entry,
              ...fields,
              unitId,
            }
          : entry
    );
  }

  return [
    ...manuscriptEntries,
    {
      manuscriptText: "",
      authorNote: "",
      status:
        "not_started",
      ...fields,
      unitId,
    },
  ];
}




function transientPageBoundaryForEdit(
  changedFields,
  request
) {
  const fields =
    safeObject(
      changedFields
    );

  const content =
    safeObject(
      fields
        .manuscriptContent
    );

  const blocks =
    safeArray(
      content.blocks
    );

  const sourceBlockId =
    clean(
      request
        ?.sourceBlockId
    );

  const continuationBlockId =
    clean(
      request
        ?.continuationBlockId
    );

  if (
    !sourceBlockId ||
    !continuationBlockId ||
    !blocks.length
  ) {
    return null;
  }

  const continuationIndex =
    blocks.findIndex(
      (block) =>
        clean(
          block?.id
        ) ===
        continuationBlockId
    );

  if (
    continuationIndex < 1
  ) {
    return null;
  }

  const sourceBlock =
    blocks[
      continuationIndex - 1
    ];

  const continuationBlock =
    blocks[
      continuationIndex
    ];

  if (
    clean(
      sourceBlock?.id
    ) !==
      sourceBlockId ||
    sourceBlock?.type ===
      "page_break" ||
    continuationBlock?.type ===
      "page_break"
  ) {
    return null;
  }

  const usedIds =
    new Set(
      blocks
        .map(
          (block) =>
            clean(
              block?.id
            )
        )
        .filter(
          Boolean
        )
    );

  const baseId =
    "__nextscenes-transient-page-boundary";

  let transientId =
    baseId;

  let suffix =
    2;

  while (
    usedIds.has(
      transientId
    )
  ) {
    transientId =
      `${baseId}-${suffix}`;

    suffix +=
      1;
  }

  const nextContent = {
    ...content,

    blocks: [
      ...blocks.slice(
        0,
        continuationIndex
      ),

      {
        id:
          transientId,

        type:
          "page_break",
      },

      ...blocks.slice(
        continuationIndex
      ),
    ],
  };

  return {
    id:
      transientId,

    changedFields: {
      ...fields,

      manuscriptContent:
        nextContent,

      manuscriptText:
        plainTextFromManuscriptContent(
          nextContent
        ),
    },
  };
}


function entriesWithoutTransientPageBoundary(
  entries,
  transientId
) {
  const boundaryId =
    clean(
      transientId
    );

  if (!boundaryId) {
    return safeArray(
      entries
    );
  }

  return safeArray(
    entries
  ).map(
    (entry) => {
      const content =
        entry
          ?.manuscriptContent;

      const blocks =
        Array.isArray(
          content
            ?.blocks
        )
          ? content.blocks
          : null;

      if (!blocks) {
        return entry;
      }

      const nextBlocks =
        blocks.filter(
          (block) =>
            !(
              block?.type ===
                "page_break" &&
              clean(
                block?.id
              ) ===
                boundaryId
            )
        );

      if (
        nextBlocks.length ===
        blocks.length
      ) {
        return entry;
      }

      const nextContent = {
        ...content,

        blocks:
          nextBlocks,
      };

      return {
        ...entry,

        manuscriptContent:
          nextContent,

        manuscriptText:
          plainTextFromManuscriptContent(
            nextContent
          ),
      };
    }
  );
}


/*
 * Pure local manuscript-edit bridge.
 *
 * Writer authority comes first:
 * the active edit is always retained in nextEntries.
 *
 * Existing-page automatic flow is then attempted as a local
 * enhancement. If it cannot be applied safely, the edit remains
 * intact and the existing capacity/advisory workflow may respond.
 *
 * This function does not persist, create Book Structure units,
 * invoke React callbacks, or mutate supplied values.
 */
export function prepareChildrensBookManuscriptLiveReflow({
  units = [],
  entries = [],
  activeUnitId = "",
  changedFields = {},
  wordLimit,
  pageFlowBoundaryRequest = null,
} = {}) {
  const unitId =
    clean(
      activeUnitId
    );

  if (!unitId) {
    return {
      ok: false,
      flowApplied: false,
      code:
        "active_unit_required",
      error:
        "Choose an active manuscript page before applying a local manuscript edit.",
      nextEntries:
        safeArray(
          entries
        ),
    };
  }

  const editedEntries =
    entriesWithActiveEdit({
      entries,
      activeUnitId:
        unitId,
      changedFields,
    });


  const transientBoundary =
    pageFlowBoundaryRequest
      ? transientPageBoundaryForEdit(
          changedFields,
          pageFlowBoundaryRequest
        )
      : null;

  if (
    pageFlowBoundaryRequest &&
    !transientBoundary
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        "page_flow_boundary_invalid",
      nextEntries:
        editedEntries,
      application:
        null,
    };
  }

  const flowEntries =
    transientBoundary
      ? entriesWithActiveEdit({
          entries,
          activeUnitId:
            unitId,
          changedFields:
            transientBoundary
              .changedFields,
        })
      : editedEntries;


  const application =
    prepareChildrensBookManuscriptFlowApplication({
      units,
      entries:
        flowEntries,
      activeUnitId:
        unitId,
      wordLimit,
    });

  if (
    !application?.ok ||
    !application
      .applicable
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        application?.code ||
        application?.reason ||
        "automatic_flow_not_applicable",
      nextEntries:
        editedEntries,
      application,
    };
  }

  const nextEntries =
    transientBoundary
      ? entriesWithoutTransientPageBoundary(
          application
            .nextEntries,
          transientBoundary
            .id
        )
      : application
          .nextEntries;

  const returnedApplication =
    transientBoundary
      ? {
          ...application,
          nextEntries,
        }
      : application;


  return {
    ok: true,
    flowApplied: true,
    reason:
      "existing_page_flow_applied",
    nextEntries:
      nextEntries,
    application:
      returnedApplication,
  };
}
